import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2
} from "lucide-react";
import {
  Button,
  IconButton,
  Input,
  Table,
  Badge,
  EmptyState,
  PageHeader,
  Tooltip
} from "../../components/ui";
import PincodeRateModal from "../../components/transport/PincodeRateModal";
import {
  getAllPincodeRates,
  getAllPincodeRatesPortal,
  deletePincodeRate,
  deletePincodeRatePortal
} from "../../api/transport/transportApi";
import {
  useToast
} from "../../context/ToastContext";

const PincodeRateList = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await getAllPincodeRatesPortal()
        : await getAllPincodeRates();
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load pincode rates");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pincode rate?")) return;
    setDeletingId(id);
    try {
      if (isPortal) await deletePincodeRatePortal(id);
      else await deletePincodeRate(id);
      toast.success("Pincode rate deleted.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (r.pincode || "").toLowerCase().includes(q) ||
      (r.areaName || "").toLowerCase().includes(q) ||
      (r.vehicleType || "").toLowerCase().includes(q)
    );
  });

  const columns = [
    { key: "pincode", header: "Pincode", sortable: true },
    {
      key: "areaName",
      header: "Area",
      render: (row) => row.areaName || "—",
    },
    {
      key: "vehicleType",
      header: "Vehicle type",
      render: (row) => (
        <Badge variant="primary">{(row.vehicleType || "").replace(/_/g, " ")}</Badge>
      ),
    },
    {
      key: "monthlyCharge",
      header: "Monthly charge",
      render: (row) => (
        <span className="font-medium">
          ₹{Number(row.monthlyCharge || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      render: (row) => (
        <Badge variant={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip content="Edit">
            <IconButton
              aria-label="Edit rate"
              variant="ghost"
              onClick={() => {
                setEditData(row);
                setModalOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          <Tooltip content="Delete">
            <IconButton
              aria-label="Delete rate"
              variant="ghost"
              disabled={deletingId === row._id}
              onClick={() => handleDelete(row._id)}
            >
              <Trash2 className="h-4 w-4 text-danger-500" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <PageHeader
        title="Transport Pincode Rates"
        description="Define monthly charges by pincode and vehicle type"
        actions={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditData(null);
              setModalOpen(true);
            }}
          >
            Add rate
          </Button>
        }
      />

      
      

      <Input
        placeholder="Search pincode, area, vehicle type..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <Table
        columns={columns}
        data={filtered}
        isLoading={loading}
        emptyState={
          <EmptyState
            title="No pincode rates yet"
            description="Add rates so transport charges can be auto-detected on assignment."
            action={
              <Button
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setEditData(null);
                  setModalOpen(true);
                }}
              >
                Add rate
              </Button>
            }
          />
        }
      />

      <PincodeRateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          toast.success(editData ? "Rate updated." : "Rate created.");
          fetchData();
        }}
        editData={editData}
        isPortal={isPortal}
      />
    </div>
  );
};

export default PincodeRateList;
